import { SUBSCRIPTION_EXP_ENABLED_HEADER } from '@src/constants/api.headers.constants'
import { createBatches } from '@src/utils/general.utils'

import Product from '../../domains/Product/Product'
import QuinceApiError from '../../domains/quince-api-error'
import filterFalsy from '../../utils/filterFalsy'
import { getContentProductsEnv } from '../../utils/getEnvironmentParam'
import { sandServicesHttpClient } from '../../utils/HttpClient'

import { adaptEnrichV2Product } from './adapt-enrich-v2-product'
import { IEnrichV2Product } from './enrich-v2.types'

import type { ProductServiceOptions } from './productService.types'

/**
 * Request for `GET /products/v2/enrich`, at param parity with the v1 `GetProductRequest`.
 *
 * The three id spaces are the ONLY breaking rename in the contract, and the collision is the
 * dangerous part: v1's `productIds` meant Shopify SPIDs, v2's `productIds` means INTERNAL ids.
 * The field names here stay in the v1 vocabulary (`spids`, `contentfulProductIds`) so callers
 * migrating off `GetProductRequest` cannot silently swap id spaces; the wire keys are mapped in
 * `fetchAndEnrichProductsV2` below:
 *
 *   v1 wire param        →  v2 wire param
 *   productIds           →  spIds                (Shopify product ids)
 *   internalProductIds   →  productIds           (internal SAND product ids)
 *   contentfulProductIds →  contentfulProductIds (unchanged)
 */
export interface GetProductV2Request {
  /**
   * INTERNAL product ids — wire param `productIds`. Maps to what v1 called
   * `internalProductIds` (v1's `productIds` meant Shopify SPIDs).
   */
  productIds?: (number | string)[]
  /**
   * Shopify product ids — wire param `spIds`. Renamed from `shopifyProductIds`: the enrich
   * contract is dropping the `shopify` prefix from its param names; same id space as v1's
   * `productIds`.
   */
  spids?: (number | string)[]
  /** Contentful product ids — wire param unchanged from v1. */
  contentfulProductIds?: string[]
  /** v1-style enrich options, comma-joined onto the wire exactly as v1 sends them. */
  options?: ProductServiceOptions[]
  useLite?: boolean
  /** Delivery-estimate zip, forwarded for v1 parity. */
  zipCode?: string
  extraParams?: object
}

export type GetProductV2RequestWithProductIds = GetProductV2Request & Required<Pick<GetProductV2Request, 'productIds'>>
export type GetProductV2RequestWithSpids = GetProductV2Request & Required<Pick<GetProductV2Request, 'spids'>>
export type GetProductV2RequestWithContentfulProductIds = GetProductV2Request &
  Required<Pick<GetProductV2Request, 'contentfulProductIds'>>

/**
 * Smart Variant / VDA v2 enrich — `GET /products/v2/enrich`. Called only when
 * the SVD rollout experiment (`ramp_svd-rollout-web`) resolves to `show` — see
 * `cart.service.getProductData` and `checkout.service.getProductData`; flag-off traffic stays on the
 * v1 `fetchAndEnrichProducts` path untouched.
 *
 * Every caller names v2 at its own call site behind its own flag check. `ProductService.getProducts`
 * deliberately does NOT route here — see the "stays on v1 unconditionally" note there for why a
 * funnel-wide flip is not safe (dropped `swatchProducts`, unsupported `useLite`, no `/b2b` path).
 *
 * Responses are adapted back to the v1 `IProduct` shape (svid/spid renames,
 * options/title synthesis from `vdas`) so consumers work unchanged. Note the adapter passes `vdas`
 * THROUGH, so `isSvdDomainProduct` reports true for a v2-fetched product even on this backward
 * path — SVD-aware renderers switch to their SVD branch by design.
 *
 * No `/b2b` prefix — v2 has no `/b2b` path at all, so every caller must exclude B2B self-serve
 * (`!getStoreProgramIdFromStorage()`) rather than expecting a b2b-prefixed v2 URL.
 */
export async function fetchAndEnrichProductsV2(
  fetchProductRequest?: GetProductV2Request,
  subscriptionExpEnabled?: boolean,
): Promise<Product[]> {
  // useLite defaults to false: the trimmed v2 lite response is still being
  // built by BE — every v2 caller fetches the full payload until BE confirms
  // lite keeps vdas / attributeValues / media / status / ids.
  const {
    productIds = undefined,
    spids = undefined,
    contentfulProductIds = undefined,
    options = undefined,
    useLite = false,
    zipCode = undefined,
    extraParams = undefined,
  } = fetchProductRequest ?? {}
  // Guards on EVERY id space, mirroring v1: a spid- or contentful-only request must reach the
  // wire, not fall out here as an empty array.
  if ((productIds?.length ?? 0) === 0 && (spids?.length ?? 0) === 0 && (contentfulProductIds?.length ?? 0) === 0) {
    return []
  }

  let productList: IEnrichV2Product[] = []
  try {
    const { data: products } = await sandServicesHttpClient.get<IEnrichV2Product[]>(`/products/v2/enrich`, {
      params: {
        ...extraParams,
        productIds: productIds?.join(','),
        spIds: spids?.join(','),
        contentfulProductIds: contentfulProductIds?.join(','),
        options: options?.join(','),
        zipCode: zipCode,
        environment: getContentProductsEnv(),
        useLite,
      },
      headers: {
        [SUBSCRIPTION_EXP_ENABLED_HEADER]: subscriptionExpEnabled ? 'true' : 'false',
      },
    })
    productList = products
  } catch (e) {
    throw new QuinceApiError({
      error: e as Error,
      message: 'Failed while fetching products v2 enrich fetchAndEnrichProductsV2() runtime',
      apiName: 'PRODUCTS_ENRICH_V2_FETCH',
      data: {
        fetchProductRequest,
      },
    })
  }

  return filterFalsy(productList).map(product => new Product(adaptEnrichV2Product(product)))
}

/**
 * One batching helper per id space, mirroring v1's three. Batching MUST stay per-id-space: the
 * batch key has to be the id list actually being sent, so a helper cannot batch `productIds` while
 * a caller also passes `spids` (every batch would re-send the full spid list).
 *
 * NOTE — only the `productIds` helper has a caller today (cart/checkout). The `spids` and
 * `contentfulProductIds` twins are kept ready for the per-surface v2 cutovers, since the id-space
 * re-keying below is the easy thing to get wrong; they are not dead ends. Wire one up from the
 * surface that needs it, never from `ProductService.getProducts`.
 */
export async function fetchAndEnrichProductsV2UsingBatching(
  fetchProductRequest: GetProductV2RequestWithProductIds,
  subscriptionExpEnabled?: boolean,
): Promise<Product[]> {
  const batches = createBatches(fetchProductRequest.productIds)
  const products = await Promise.all(
    batches.map(batch =>
      fetchAndEnrichProductsV2({ ...fetchProductRequest, productIds: batch }, subscriptionExpEnabled),
    ),
  )
  return products.flat()
}

export async function fetchAndEnrichProductsV2BySpidsUsingBatching(
  fetchProductRequest: GetProductV2RequestWithSpids,
  subscriptionExpEnabled?: boolean,
): Promise<Product[]> {
  const batches = createBatches(fetchProductRequest.spids)
  const products = await Promise.all(
    batches.map(batch => fetchAndEnrichProductsV2({ ...fetchProductRequest, spids: batch }, subscriptionExpEnabled)),
  )
  return products.flat()
}

export async function fetchAndEnrichProductsV2ByContentfulIdsUsingBatching(
  fetchProductRequest: GetProductV2RequestWithContentfulProductIds,
  subscriptionExpEnabled?: boolean,
): Promise<Product[]> {
  const batches = createBatches(fetchProductRequest.contentfulProductIds)
  const products = await Promise.all(
    batches.map(batch =>
      fetchAndEnrichProductsV2({ ...fetchProductRequest, contentfulProductIds: batch }, subscriptionExpEnabled),
    ),
  )
  return products.flat()
}
